import { action, computed, makeObservable, observable, when } from 'mobx'
import { getClickWrapListToAccept } from '../firestore/ClickWrap'
import { ClickWrap } from '../models/ClickWrap'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { ClickWrapRole } from '../types'
import { UserProfileRole, type StaticModelCollection } from '../types'
import { Cubit } from './core'
import type { BreakoutUser } from '../models/BreakoutUser'
import { ClickWrapAccepted } from '../models/ClickWrapAccepted'
import {
  acceptClickWraps,
  getClickWrapAcceptedList,
} from '../firestore/ClickWrapAccepted'

export class ClickWrapsCubit extends Cubit {
  private repository: FirebaseRepository
  private clickWraps: StaticModelCollection<ClickWrap>
  private clickWrapsAccepted: StaticModelCollection<ClickWrapAccepted>
  private user: BreakoutUser

  // This is a simple state machine state.
  // It begins in the 'loading' state.
  // From 'loading':
  //    it can transition to 'must_accept' if there are click wraps to accept.
  //    it can transition to 'done' if there are no click wraps to accept.
  // From 'must_accept':
  //    it can transition to 'done' if the user has accepted all click wraps.
  // From 'done':
  //    it cannot transition to any other state.
  @observable
  clickWrapState: 'loading' | 'must_accept' | 'done' = 'loading'

  constructor(
    repository: FirebaseRepository,
    { user }: { user: BreakoutUser }
  ) {
    super()
    this.repository = repository
    this.clickWraps = ClickWrap.emptyCollection(this.repository)
    this.clickWrapsAccepted = ClickWrapAccepted.emptyCollection(this.repository)
    this.user = user
    makeObservable(this)
  }

  initialize(): void {
    when(
      () => this.user.isLoaded,
      () => {
        if (!this.user.role) {
          this.loadedWithoutClickWraps()
          return
        }

        if (this.user.isAnonymous) {
          this.loadedWithoutClickWraps()
          return
        }

        const clickWrapRoles = calculcateClickWrapRoles(this.user.role)

        if (clickWrapRoles.length === 0) {
          this.loadedWithoutClickWraps()
          return
        }
        this.addStream(
          getClickWrapListToAccept(this.repository, clickWrapRoles),
          (models) => this.clickWraps.replaceModels(models)
        )
        this.addStream(
          getClickWrapAcceptedList(this.repository, { userId: this.user.uid }),
          (models) => this.clickWrapsAccepted.replaceModels(models)
        )
      }
    )

    when(
      () => this.clickWraps.isLoaded && this.clickWrapsAccepted.isLoaded,
      () => {
        if (this.clickWrapsToAccept.length === 0) {
          this.loadedWithoutClickWraps()
        } else {
          this.loadedWithClickWraps()
        }
      }
    )

    when(
      () => this.clickWrapState === 'done',
      () => {
        this.closeAllStreams()
      }
    )
  }

  @computed
  get clickWrapsToAccept(): ClickWrap[] {
    return this.clickWraps.models.filter(
      (clickWrap) =>
        !this.clickWrapsAccepted.models.some(
          (clickWrapAccepted) =>
            clickWrapAccepted.data.clickWrapId === clickWrap.id &&
            clickWrapAccepted.data.acceptedAt.valueOf() >=
              clickWrap.data.clickWrapDate.valueOf()
        )
    )
  }

  async acceptClickWraps(): Promise<void> {
    await acceptClickWraps(this.repository, {
      clickWrapIds: this.clickWrapsToAccept.map((clickWrap) => clickWrap.id),
    })
    this.acceptedClickWraps()
  }

  @action
  loadedWithClickWraps(): void {
    if (this.clickWrapState !== 'loading') return
    this.clickWrapState = 'must_accept'
  }

  @action
  loadedWithoutClickWraps(): void {
    if (this.clickWrapState !== 'loading') return
    this.clickWrapState = 'done'
  }

  @action
  acceptedClickWraps(): void {
    if (this.clickWrapState !== 'must_accept') return
    this.clickWrapState = 'done'
  }
}

function calculcateClickWrapRoles(role: UserProfileRole): ClickWrapRole[] {
  switch (role) {
    case UserProfileRole.admin:
      return []
    case UserProfileRole.author:
      return []
    case UserProfileRole.corre:
      return []
    case UserProfileRole.editor:
      return []
    case UserProfileRole.instructor:
      return [ClickWrapRole.instructor]
    case UserProfileRole.ta:
      return [ClickWrapRole.instructor]
    case UserProfileRole.student:
      return [ClickWrapRole.participant]
  }
}
